import React from 'react';
import { Link, Routes, Route } from 'react-router-dom';
import { Shield, Globe, AlertTriangle } from 'lucide-react';
import { CodeBlock } from '../components/code/CodeBlock';
import { GatewayWebhook } from './GatewayWebhook';

const GatewayCheckoutHome: React.FC = () => {
  const paymentMethods = [
    { method: 'AkuPay', status: 'Coming Soon', available: false },
    { method: 'Card', status: 'Available', available: true },
    { method: 'Bank Transfer', status: 'Available', available: true },
    { method: 'Direct Debit', status: 'Coming Soon', available: false },
    { method: 'Opay', status: 'Available', available: true },
    { method: 'PalmPay', status: 'Available', available: true }
  ];

  const environments = [
    { name: 'Staging (Test)', url: 'https://sandbox-api.nectargateway.com' },
    { name: 'Live (Production)', url: 'https://api.nectargateway.com' }
  ];

  const apiKeyTypes = [
    { type: 'Public Key', useCase: 'For client-side initialization (if applicable)' },
    { type: 'Secret Key', useCase: 'For server-side transactions and verification' }
  ];

  const initializeRequestParams = [
    { name: 'amount', type: 'integer', required: true, description: 'Amount in the smallest currency unit (e.g., kobo).' },
    { name: 'email', type: 'string', required: true, description: 'Customer\'s email address.' },
    { name: 'reference', type: 'string', required: true, description: 'Unique identifier for the transaction (UUID).' },
    { name: 'currency', type: 'string', required: false, description: 'Currency code (e.g., NGN, USD).' },
    { name: 'callbackUrl', type: 'string', required: false, description: 'URL to which the payment status will be sent.' },
    { name: 'webhookUrl', type: 'string', required: false, description: 'URL for webhook notification' },
    { name: 'channels', type: 'array', required: false, description: 'Allowed payment channels (e.g card, bank transfer, opay, palmpay, direct debit).' },
    { name: 'bearer', type: 'integer', required: false, description: 'Who bears the transaction fee (1 = merchant, 2 = customer).' },
    { name: 'metadata', type: 'object', required: false, description: 'Optional data for record-keeping.' }
  ];

  const statusCodes = [
    { code: 0, status: 'COMPLETED', description: 'Transaction completed successfully.' },
    { code: 1, status: 'PENDING', description: 'Transaction awaiting confirmation.' },
    { code: 2, status: 'AUTHORIZED', description: 'Transaction authorized but not captured.' },
    { code: 3, status: 'CAPTURED', description: 'Funds successfully captured.' },
    { code: 4, status: 'DECLINED', description: 'Transaction declined by processor.' },
    { code: 5, status: 'CANCELED', description: 'Transaction canceled by user.' },
    { code: 6, status: 'REFUNDED', description: 'Transaction refunded to the customer.' },
    { code: 7, status: 'CHARGED_BACK', description: 'Transaction reversed due to chargeback.' },
    { code: 8, status: 'IN_PROGRESS', description: 'Transaction currently processing.' },
    { code: 9, status: 'VOIDED', description: 'Transaction voided.' },
    { code: 10, status: 'PENDING_REVIEW', description: 'Awaiting manual review.' },
    { code: 17, status: 'SCHEDULED', description: 'Future/recurring transaction.' }
  ];

  const initializeRequestJson = `{
    "amount": 100000,
    "email": "example@gmail.com",
    "currency": "NGN",
    "reference": "{{$randomUUID}}",
    "callbackUrl": "{{$randomUrl}}",
    "channels": [
        "card",
      "bank"
    ],
    "bearer": 1,
    "webhookUrl": "{{$randomUrl}}",
    "metadata": {}
}`;

  const initializeResponseJson = `{
  "status": 200,
  "message": "Request successfully processed",
  "data": {
    "authorizationUrl": "https://nectargateway.com/abc123xyz",
    "reference": "txn_123456789",
    "accessCode": "abc123xyz"
  }
}`;

  const verifyResponseJson = `{
  "status": 200,
  "message": "Request successfully processed",
  "data": {
    "merchant": "Nectarlive",
    "reference": "txn_123456789",
    "merchantRef": "2d059b6a-4626-47e2-8f35-f903ce7a5f22",
    "grossAmount": 50.75,
    "requestedAmount": 50.00,
    "transactionFee": 0.75,
    "settlementAmount": 50.00,
    "customerId": "example@gmail.com",
    "currencyCode": "NGN",
    "callbackUrl": "https://merchant.com/callback",
    "authorizationType": "3DSecure",
    "transactionDate": "2025-03-10T14:04:15Z",
    "channel": "Card",
    "status": 2
  }
}`;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Gateway Checkout</h1>
        
        {/* Overview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Overview</h2>
          <p className="text-white-muted mb-6 leading-relaxed">
            Nectar payment gateway enables merchants to accept payments seamlessly through multiple channels. 
            The system provides reliable APIs for initializing, processing, and verifying payments securely.
          </p>
        </div>

        {/* Supported Payment Methods & Channels */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Supported Payment Methods & Channels</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Payment Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {paymentMethods.map((method, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">{method.method}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          method.available 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {method.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Environments */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Environments</h2>
          
          <h3 className="text-lg font-semibold text-white mb-4">Base URLs</h3>
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Environment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Base URL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {environments.map((env, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">{env.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{env.url}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Security</h2>
          
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <Shield className="h-5 w-5 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold text-red-500">Security Requirements</h3>
            </div>
            <ul className="text-sm text-red-500/90 space-y-2">
              <li>• All API requests must use HTTPS.</li>
              <li>• Transactions are secured via API key authentication.</li>
              <li>• Sensitive information such as card data should never be handled on the client side.</li>
            </ul>
          </div>
        </div>

        {/* Authentication */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Authentication</h2>
          <p className="text-white-muted mb-4">
            All API requests require your unique API key in the Authorization header.
          </p>
          
          <h3 className="text-lg font-semibold text-white mb-4">Header Example:</h3>
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <code className="text-lime text-sm">
              Authorization: &lt;your_public_or_secret_api_key&gt;<br/>
              Content-Type: application/json
            </code>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">API Key Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Use Case</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {apiKeyTypes.map((keyType, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">{keyType.type}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{keyType.useCase}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-500 mb-2">Important</h3>
                <p className="text-sm text-yellow-500/90">
                  Keep your secret key safe and never expose it in client-side code.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Initialize a Transaction */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Initialize a Transaction</h2>
          <p className="text-white-muted mb-6">
            Creates a new transaction and returns a payment URL for the customer to complete payment.
          </p>
          
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Endpoint:</h3>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <span className="bg-green-600 text-white px-3 py-1 rounded text-sm font-mono mr-3">POST</span>
                <code className="text-lime font-mono">/transaction/initialize</code>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-white mb-4">Request Body (JSON)</h3>
          <CodeBlock code={initializeRequestJson} language="json" />

          <h3 className="text-lg font-semibold text-white mb-4 mt-6">Request Parameters</h3>
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Required</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {initializeRequestParams.map((param, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{param.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{param.type}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          param.required 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {param.required ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-white-muted">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-white mb-4">Response (JSON)</h3>
          <CodeBlock code={initializeResponseJson} language="json" />
        </div>

        {/* Redirect Behavior */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Redirect Behavior</h2>
          <p className="text-white-muted mb-4">
            After payment, Nectar-Gateway redirects the customer to your callback URL with transaction details:
          </p>
          
          <div className="bg-gray-800 rounded-lg p-4">
            <code className="text-lime text-sm break-all">
              https://merchant.com/callback?transactionReference=abc123&status=success&errorMessage=&reference=xyz987
            </code>
          </div>
        </div>

        {/* Verify a Transaction */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Verify a Transaction</h2>
          <p className="text-white-muted mb-6">
            Use this endpoint to confirm the final status of a transaction.
          </p>
          
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Endpoint:</h3>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-mono mr-3">GET</span>
                <code className="text-lime font-mono">/transaction/verify/{'{transactionReference}'}</code>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-white mb-4">Response (JSON)</h3>
          <CodeBlock code={verifyResponseJson} language="json" />
        </div>

        {/* Status Codes */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Status Codes</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {statusCodes.map((status, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{status.code}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">{status.status}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{status.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-lime text-dark rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Globe className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold">What's Next</h2>
          </div>
          <p className="text-dark/90 mb-4">
            Learn about webhook notifications for real-time payment updates
          </p>
          <Link
            to="/gateway-checkout/webhook"
            className="inline-flex items-center px-6 py-2 bg-dark text-white font-medium rounded-lg hover:bg-dark/90 transition-colors"
          >
            Webhook
          </Link>
        </div>
      </div>
    </div>
  );
};

export const GatewayCheckout: React.FC = () => {
  return (
    <Routes>
      <Route index element={<GatewayCheckoutHome />} />
      <Route path="webhook" element={<GatewayWebhook />} />
    </Routes>
  );
};